/**
 * Arquivo: src/App.tsx
 * Versão: 6.0.0
 * Data: 08/10/2026
 * Descrição: Mantém o splash institucional até concluir a verificação inicial da sessão.
 */
import { useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import AppLayout from './components/AppLayout'
import Dashboard from './pages/Dashboard'
import Login from './pages/Login'
import TrocaSenhaObrigatoria from './pages/TrocaSenhaObrigatoria'
import ResetPassword from './pages/ResetPassword'
import Placeholder from './pages/Placeholder'
import Contas from './pages/Contas'
import PlanoContas from './pages/PlanoContas'
import Lancamentos from './pages/Lancamentos'
import ContasFinanceiras from './pages/ContasFinanceiras'
import Transferencias from './pages/Transferencias'
import Investimentos from './pages/Investimentos'
import Recorrencias from './pages/Recorrencias'
import Cartoes from './pages/Cartoes'
import Orcamentos from './pages/Orcamentos'
import Metas from './pages/Metas'
import Relatorios from './pages/Relatorios'
import Fiscal from './pages/Fiscal'
import Conciliacao from './pages/Conciliacao'
import Administracao from './pages/Administracao'
import Configuracoes from './pages/Configuracoes'
import Auditoria from './pages/Auditoria'
import { neonAuth, type NeonAuthSession } from './services/neonAuth'
import { obterMinhaIdentidade, type MinhaIdentidade } from './services/auth'

function ocultarSplash() {
  const splash = document.getElementById('app-splash')
  if (!splash || splash.classList.contains('is-hiding')) return

  splash.classList.add('is-hiding')
  window.setTimeout(() => splash.remove(), 260)
}

export default function App() {
  const [session, setSession] = useState<NeonAuthSession | null>(null)
  const [checking, setChecking] = useState(true)
  const [identidade, setIdentidade] = useState<MinhaIdentidade | null>(null)
  const [checkingIdentidade, setCheckingIdentidade] = useState(false)

  useEffect(() => {
    let ativo=true
    const carregarSessao=async()=>{
      try{const data=await neonAuth.getSession();if(ativo)setSession(data.session)}
      catch{if(ativo)setSession(null)}
      finally{if(ativo)setChecking(false)}
    }
    void carregarSessao()
    const unsubscribe=neonAuth.subscribe(()=>{void carregarSessao()})
    return()=>{ativo=false;unsubscribe()}
  },[])

  useEffect(() => {
    if (!session) {
      setIdentidade(null)
      setCheckingIdentidade(false)
      return
    }

    let ativo = true
    setCheckingIdentidade(true)
    obterMinhaIdentidade()
      .then(async info => {
        if (!ativo) return
        if (!info.active) {
          await neonAuth.signOut()
          return
        }
        setIdentidade(info)
      })
      .catch(error => {
        console.error('Falha ao carregar identidade no Worker:', error)
        if (ativo) setIdentidade(null)
      })
      .finally(() => {
        if (ativo) setCheckingIdentidade(false)
      })

    return () => { ativo = false }
  }, [session])

  const recarregarIdentidade = async () => {
    const info = await obterMinhaIdentidade()
    setIdentidade(info)
  }

  useEffect(() => {
    if (!checking) {
      requestAnimationFrame(() => {
        requestAnimationFrame(ocultarSplash)
      })
    }
  }, [checking])

  if (window.location.pathname === '/reset-password') return <ResetPassword />
  if (checking || checkingIdentidade) return null
  if (!session) return <Login />
  if (identidade?.mustChangePassword) {
    return <TrocaSenhaObrigatoria email={identidade.email} onConcluida={recarregarIdentidade}/>
  }

  const page = (titulo: string) => <Placeholder titulo={titulo} />

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/pagar" element={<ContasFinanceiras tipo="DESPESA" />} />
        <Route path="/receber" element={<ContasFinanceiras tipo="RECEITA" />} />
        <Route path="/transferencias" element={<Transferencias />} />
        <Route path="/cartoes" element={<Cartoes />} />
        <Route path="/lancamentos" element={<Lancamentos />} />
        <Route path="/investimentos" element={<Investimentos />} />
        <Route path="/contas" element={<Contas />} />
        <Route path="/plano-contas" element={<PlanoContas />} />
        <Route path="/recorrencias" element={<Recorrencias />} />
        <Route path="/orcamentos" element={<Orcamentos />} />
        <Route path="/metas" element={<Metas />} />
        <Route path="/relatorios" element={<Relatorios />} />
        <Route path="/fiscal" element={<Fiscal />} />
        <Route path="/conciliacao" element={<Conciliacao />} />
        <Route path="/administracao" element={<Administracao />} />
        <Route path="/auditoria" element={<Auditoria />} />
        <Route path="/configuracoes" element={<Configuracoes />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  )
}
